<template>
    <VTextField
        v-model="val"
        @change="handleChange"
        :variant="variant"
        :label="label"
        :placeholder="placeholder"
        :persistentPlaceholder="persistent"
        :density="density"
        :hint="hint"
        :prefix="prefix"
        :suffix="suffix"
        :messages="messages"
        :disabled="disabled"
        :clearable="clearable"
        :readonly="readonly"
        :loading="loading"
        :errorMessages="errorMessages"
        :autofocus="autofocus"
        :autocomplete="autocomplete">
        <template #append-inner v-if="tooltip || showValidCheck">
            <Icon v-if="showValidCheck" type="Check" color="green" />
            <Icon v-if="tooltip" type="InfoCircle" color="black" :tooltip="tooltip" />
        </template>
    </VTextField>
</template>

<script lang="ts">
    import { computed, defineComponent, watch, ref, PropType } from 'vue';
    import { VTextField } from 'vuetify/components';
    import { Variant, Density, AutocompleteOption } from './Textbox.interfaces';
    import { capitalizeWords } from '@shared/utils/string';
    import { isNil } from 'lodash';
    import Icon from '@ui/components/Icon/Icon.vue';

    export default defineComponent({
        name: 'Textbox',
        components: { VTextField, Icon },
        emits: ['update:modelValue'],
        props: {
            modelValue: { type: String },
            label: String,
            placeholder: String,
            hint: String,
            prefix: String,
            suffix: String,
            loading: { type: Boolean as PropType<boolean>, default: false },
            disabled: { type: Boolean as PropType<boolean>, default: false },
            clearable: { type: Boolean as PropType<boolean>, default: false },
            readonly: { type: Boolean as PropType<boolean>, default: false },
            capitalize: { type: Boolean as PropType<boolean>, default: false },
            messages: { type: Array as PropType<string[]> },
            errorMessages: {
                type: [Array, String] as PropType<string[] | string>,
            },
            variant: { type: String as PropType<Variant>, default: 'outlined' },
            density: { type: String as PropType<Density>, default: 'default' },
            autofocus: { type: Boolean as PropType<boolean>, default: false },
            autocomplete: { type: String as PropType<AutocompleteOption> },
            tooltip: { type: String },
            showValidCheck: { type: Boolean },
        },
        setup(props, { emit }) {
            const val = ref<string | undefined>(props.modelValue);
            const persistent = computed(() => Boolean(val.value));

            function handleChange() {
                if (val.value) {
                    val.value = val.value.trim();
                }
            }

            watch(
                () => props.modelValue,
                (newValue, prevValue) => {
                    if (newValue !== prevValue) {
                        val.value = newValue;
                    }
                }
            );

            watch(
                () => val.value,
                (newValue, prevValue) => {
                    if (!isNil(newValue) && newValue !== prevValue) {
                        emit(
                            'update:modelValue',
                            props.capitalize ? capitalizeWords(newValue) : newValue
                        );
                    }
                }
            );

            return {
                val,
                persistent,
                handleChange,
            };
        },
    });
</script>
